import { API_PREFIX, paginatedFromDto, type HttpClient } from "@nasam/kernel";
import type { AlertsRepository, CreateAlertInput, UpdateAlertInput } from "../domain";
import {
  alertCategoriesResponseDtoSchema,
  alertDeleteResponseDtoSchema,
  alertDetailResponseDtoSchema,
  alertRecipientsResponseDtoSchema,
  alertSavedResponseDtoSchema,
  alertsListResponseDtoSchema,
} from "./alertDtos";
import { alertDetailFromDto, alertListItemFromDto } from "./alertMappers";

const ALERTS_PATH = `${API_PREFIX}/alerts`;

/** Drops the keys left undefined, so the request carries only what was set. */
function withoutUndefined<T extends object>(value: T): T {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined),
  ) as T;
}

/** A create/update body: the trigger nested without its unset fields. */
function body(input: CreateAlertInput | UpdateAlertInput) {
  return { ...withoutUndefined(input), trigger: withoutUndefined(input.trigger) };
}

function saved(response: unknown) {
  const parsed = alertSavedResponseDtoSchema.parse(response);
  return {
    id: parsed.data.id,
    ...(parsed.message === undefined ? {} : { message: parsed.message }),
  };
}

export function createAlertsHttpRepository(http: HttpClient): AlertsRepository {
  return {
    async list(query) {
      const response = await http.get(ALERTS_PATH, { params: query });
      return paginatedFromDto(
        alertsListResponseDtoSchema.parse(response),
        alertListItemFromDto,
      );
    },
    async getById(id) {
      const response = await http.get(`${ALERTS_PATH}/${id}`);
      return alertDetailFromDto(alertDetailResponseDtoSchema.parse(response).data);
    },
    async create(input) {
      return saved(await http.post(ALERTS_PATH, body(input)));
    },
    async update(id, input) {
      return saved(await http.patch(`${ALERTS_PATH}/${id}`, body(input)));
    },
    async delete(id) {
      const response = await http.delete(`${ALERTS_PATH}/${id}`);
      return alertDeleteResponseDtoSchema.parse(response ?? {}).message;
    },
    async listCategories() {
      const response = await http.get(`${ALERTS_PATH}/categories`);
      return alertCategoriesResponseDtoSchema.parse(response).data;
    },
    async listRecipients(accountId) {
      const response = await http.get(`${ALERTS_PATH}/recipients`, {
        params: accountId === undefined ? {} : { accountId },
      });
      return alertRecipientsResponseDtoSchema.parse(response).data;
    },
  };
}
